import React, { useEffect, useState } from 'react';
import axios from '../axios-config';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, CheckCircle2, Clock, MessageSquare, Send, ShieldCheck, ShieldOff, Calendar, Camera, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  site_visit_requested: 'Site Visit Requested',
  owner_approved: 'Owner Approved',
  society_approved: 'Both Approved',
  scheduled: 'Visit Scheduled',
  completed: 'Visit Completed',
  scope_finalized: 'Scope Finalized',
  quotation_submitted: 'Quotation Submitted',
};

export default function FlatAssistanceWorkflow() {
  const { qqId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledSlot, setScheduledSlot] = useState('10:00-12:00');
  const [visitNotes, setVisitNotes] = useState('');
  const [scopeSummary, setScopeSummary] = useState('');
  const [scopeEstimate, setScopeEstimate] = useState('');
  const [accessError, setAccessError] = useState(null);

  const load = async () => {
    setLoading(true);
    setAccessError(null);
    try {
      const { data: d } = await axios.get(`${API}/flat-assistance/${qqId}`);
      setData(d);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403 || status === 404) {
        setAccessError(e.response?.data?.detail || (status === 403 ? 'You do not have access to this request.' : 'Request not found.'));
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load');
      }
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [qqId]);

  const role = data?._viewer_role || (window.__authRole) || null;

  const call = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (accessError) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="fa-access-denied">
      <div className="text-base font-bold text-navy mb-2">Access Denied</div>
      <p className="text-sm text-muted-foreground max-w-md mb-4">{accessError}</p>
      <button onClick={() => navigate(-1)} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold inline-flex items-center" data-testid="fa-access-denied-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
      </button>
    </div>
  );
  if (!data) return null;

  const si = data.site_inspection || {};
  const fo = data.flat_owner || {};
  const isSupplier = (window.location.pathname.includes('/supplier-dashboard'));
  const status = si.status || 'pending';
  const ownerOk = (si.owner_approval || {}).approved;
  const societyOk = (si.society_approval || {}).approved;
  const privacy = si.privacy_settings || {};

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="fa-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold text-sm inline-flex items-center"><MapPin className="h-4 w-4 mr-1.5 text-gold" /> Flat Owner Assistance</div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${status === 'scope_finalized' ? 'bg-green-100 text-green-800' : 'bg-gold text-navy'}`} data-testid="fa-status-badge">
            {STATUS_LABELS[status] || status}
          </span>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-5 max-w-3xl space-y-5">
        {/* Header card */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="fa-summary-card">
          <div className="text-base font-bold text-navy" data-testid="fa-title">{data.title}</div>
          <div className="text-xs text-muted-foreground mt-1">Category: <strong>{data.work_category}</strong> · Est. Value: ₹{(data.estimated_value || 0).toLocaleString('en-IN')}</div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="p-2 bg-muted/40 rounded" data-testid="fa-owner-name">
              <div className="text-[9px] uppercase font-bold tracking-wide text-muted-foreground">Owner</div>
              <div className="text-navy font-semibold">{fo.owner_name || '—'}</div>
            </div>
            <div className="p-2 bg-muted/40 rounded" data-testid="fa-flat-number">
              <div className="text-[9px] uppercase font-bold tracking-wide text-muted-foreground">Flat</div>
              <div className="text-navy font-semibold">{fo.flat_number || '—'}</div>
            </div>
          </div>
          {isSupplier && (privacy.mask_owner_name || privacy.mask_flat_number) && (
            <div className="mt-3 text-[11px] text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2 inline-flex items-start gap-2" data-testid="fa-privacy-notice">
              <ShieldOff className="h-3.5 w-3.5 text-amber-700 mt-0.5" />
              <span>Owner contact details are masked. Use the in-app communication panel below to coordinate.</span>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="fa-timeline-card">
          <h3 className="text-xs font-bold uppercase tracking-wide text-navy mb-3 inline-flex items-center"><Clock className="h-3.5 w-3.5 mr-1.5" /> Workflow Timeline</h3>
          <ol className="space-y-2 text-xs">
            {(si.timeline || []).map(e => (
              <li key={e.id} className="flex items-start gap-2" data-testid={`fa-timeline-event-${e.type}`}>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-navy">{(STATUS_LABELS[e.type] || e.type.replace(/_/g, ' '))}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()} · {e.actor_name || e.actor_role}</div>
                </div>
              </li>
            ))}
            {(!si.timeline || si.timeline.length === 0) && (
              <li className="text-muted-foreground text-center py-2">Workflow not yet started</li>
            )}
          </ol>
        </div>

        {/* Supplier actions */}
        {isSupplier && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="fa-supplier-actions">
            <h3 className="text-sm font-bold text-navy">Vendor Actions</h3>
            {!si.requested_at && (
              <div>
                <label className="block text-xs font-bold text-navy mb-1">Propose visit date</label>
                <div className="flex gap-2">
                  <input type="date" value={proposedDate} onChange={e => setProposedDate(e.target.value)} className="flex-1 px-3 py-2 rounded border border-input bg-background text-sm" data-testid="fa-supplier-propose-date" />
                  <button onClick={() => call('Visit requested', () => axios.post(`${API}/flat-assistance/${qqId}/site-visit-request`, { proposed_dates: [proposedDate] }))} disabled={busy || !proposedDate} className="px-3 py-2 rounded bg-orange text-white text-xs font-bold disabled:opacity-50" data-testid="fa-supplier-request-btn">Request Visit</button>
                </div>
              </div>
            )}
            {status === 'scheduled' && (
              <div>
                <label className="block text-xs font-bold text-navy mb-1">Visit notes (after visit)</label>
                <textarea value={visitNotes} onChange={e => setVisitNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" placeholder="Observations from site…" data-testid="fa-supplier-visit-notes" />
                <button onClick={() => call('Visit completed', () => axios.post(`${API}/flat-assistance/${qqId}/site-visit-complete`, { visit_notes: visitNotes, photos: [] }))} disabled={busy} className="mt-2 w-full px-3 py-2.5 rounded bg-orange text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="fa-supplier-complete-btn">
                  <Camera className="h-4 w-4 mr-1.5" /> Mark Visit Completed
                </button>
              </div>
            )}
            {status === 'completed' && (
              <div className="space-y-2">
                <label className="block text-xs font-bold text-navy">Final Scope of Work</label>
                <textarea value={scopeSummary} onChange={e => setScopeSummary(e.target.value)} rows={3} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" placeholder="Describe the finalized scope…" data-testid="fa-supplier-scope-summary" />
                <input type="number" min="0" value={scopeEstimate} onChange={e => setScopeEstimate(e.target.value)} placeholder="Total estimate (₹)" className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="fa-supplier-scope-estimate" />
                <button onClick={() => call('Scope finalized', () => axios.post(`${API}/flat-assistance/${qqId}/scope-finalize`, { scope_summary: scopeSummary, total_estimate: Number(scopeEstimate || 0) }))} disabled={busy || !scopeSummary} className="w-full px-3 py-2.5 rounded bg-navy text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="fa-supplier-finalize-btn">
                  <FileText className="h-4 w-4 mr-1.5" /> Finalize Scope
                </button>
              </div>
            )}
          </div>
        )}

        {/* Buyer (society) actions */}
        {!isSupplier && si.requested_at && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="fa-buyer-actions">
            <h3 className="text-sm font-bold text-navy">Society Actions</h3>
            {!ownerOk && (
              <button onClick={() => call('Owner approval recorded', () => axios.post(`${API}/flat-assistance/${qqId}/site-visit-approve`, { approver_party: 'owner', approved: true }))} disabled={busy} className="w-full px-3 py-2.5 rounded border-2 border-navy text-navy text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="fa-buyer-approve-owner-btn">
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Record Owner Approval
              </button>
            )}
            {!societyOk && (
              <button onClick={() => call('Society approval recorded', () => axios.post(`${API}/flat-assistance/${qqId}/site-visit-approve`, { approver_party: 'society', approved: true }))} disabled={busy} className="w-full px-3 py-2.5 rounded border-2 border-navy text-navy text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="fa-buyer-approve-society-btn">
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Record Society Approval
              </button>
            )}
            {ownerOk && societyOk && status !== 'scheduled' && status !== 'completed' && status !== 'scope_finalized' && (
              <div className="space-y-2">
                <label className="block text-xs font-bold text-navy">Schedule visit</label>
                <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="fa-buyer-schedule-date" />
                <select value={scheduledSlot} onChange={e => setScheduledSlot(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="fa-buyer-schedule-slot">
                  <option value="10:00-12:00">10:00 – 12:00</option>
                  <option value="14:00-16:00">14:00 – 16:00</option>
                  <option value="16:00-18:00">16:00 – 18:00</option>
                </select>
                <button onClick={() => call('Visit scheduled', () => axios.post(`${API}/flat-assistance/${qqId}/site-visit-schedule`, { scheduled_date: scheduledDate, scheduled_slot: scheduledSlot }))} disabled={busy || !scheduledDate} className="w-full px-3 py-2.5 rounded bg-orange text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="fa-buyer-schedule-btn">
                  <Calendar className="h-4 w-4 mr-1.5" /> Confirm Schedule
                </button>
              </div>
            )}
          </div>
        )}

        {/* Communication Panel */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="fa-messages-card">
          <h3 className="text-xs font-bold uppercase tracking-wide text-navy mb-3 inline-flex items-center"><MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Communication Panel</h3>
          <div className="space-y-2 mb-3 max-h-60 overflow-y-auto" data-testid="fa-messages-list">
            {(si.messages || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No messages yet</p>}
            {(si.messages || []).map(m => (
              <div key={m.id} className={`flex ${m.from_role === 'supplier' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.from_role === 'supplier' ? 'bg-muted text-navy' : 'bg-navy text-white'}`} data-testid={`fa-msg-${m.id}`}>
                  <div className="text-[10px] opacity-70 font-semibold">{m.from_name || m.from_role}</div>
                  <div className="text-xs">{m.message}</div>
                  <div className="text-[9px] opacity-60 mt-0.5">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" value={msg} onChange={e => setMsg(e.target.value)} placeholder="Type a message…" className="flex-1 px-3 py-2 rounded border border-input bg-background text-sm" data-testid="fa-msg-input" />
            <button onClick={async () => { if (!msg.trim()) return; setBusy(true); try { await axios.post(`${API}/flat-assistance/${qqId}/messages`, { message: msg.trim() }); setMsg(''); await load(); } catch (e) { toast.error('Send failed'); } finally { setBusy(false); } }} disabled={busy || !msg.trim()} className="px-3 py-2 rounded bg-orange text-white text-xs font-bold disabled:opacity-50 inline-flex items-center" data-testid="fa-msg-send-btn">
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
